import { useCallback, useEffect, useState } from 'react'
import { DISCIPLINES } from '../core/contracts'
import type { Discipline, SessionStats } from '../core/contracts'
import { disciplineScore, gameScore, honeScore, scoreValue } from '../core/scoring'
import type { GameContribution, HoneScore } from '../core/scoring'
import { recordDay, weekChange } from '../core/history'
import type { DaySnapshot } from '../core/history'
import { dateKey } from '../core/workout'
import { KEYS, store } from '../platform/storage'
import { GAMES } from '../registry'

/**
 * Загружает историю по всем играм и считает Hone Score.
 *
 * Живёт в app/, а не в core/: ядро не должно знать про хранилище.
 * Вся математика берётся из core/scoring, здесь только сбор данных.
 */

export interface GameProgress {
  readonly gameId: string
  readonly score: HoneScore
  readonly sessions: number
  readonly bestSpan: number
  readonly lastPlayedAt: number
}

export interface Progress {
  readonly loading: boolean
  readonly total: HoneScore
  readonly byDiscipline: Readonly<Record<Discipline, HoneScore>>
  readonly games: readonly GameProgress[]
  readonly sessionsTotal: number
  /** Дисциплина, которая отстаёт - под неё подбирается тренировка дня */
  readonly weakest: Discipline | null
  readonly history: readonly DaySnapshot[]
  /** Изменение индекса за неделю; null, если сравнивать не с чем */
  readonly weekChange: number | null
  reload: () => void
}

const EMPTY: HoneScore = { status: 'collecting', sessionsLeft: 5, provisional: null }

export function useProgress(): Progress {
  const [games, setGames] = useState<readonly GameProgress[]>([])
  const [history, setHistory] = useState<readonly DaySnapshot[]>([])
  const [loading, setLoading] = useState(true)
  const [tick, setTick] = useState(0)

  const reload = useCallback(() => setTick((n) => n + 1), [])

  useEffect(() => {
    let alive = true
    void (async () => {
      const result: GameProgress[] = []
      for (const game of GAMES) {
        let sessions: SessionStats[] = []
        try {
          sessions = await store.get<SessionStats[]>(KEYS.gameStats(game.id), [])
        } catch {
          // хранилище недоступно - считаем, что истории нет
        }
        result.push({
          gameId: game.id,
          score: gameScore(sessions, game.metric, game.difficulty),
          sessions: sessions.length,
          bestSpan: sessions.reduce((max, h) => Math.max(max, h.span), 0),
          lastPlayedAt: sessions[sessions.length - 1]?.at ?? 0,
        })
      }
      // снимок за сегодня пишем прямо здесь: recordDay заменяет запись
      // за ту же дату, поэтому повторные загрузки экрана безопасны
      const byDiscipline = collectAll(result)
      const today = honeScore(DISCIPLINES.map((d) => byDiscipline[d]))
      const todayValue = scoreValue(today)

      let saved: DaySnapshot[] = []
      try {
        saved = await store.get<DaySnapshot[]>(KEYS.indexHistory, [])
        if (todayValue !== null) {
          saved = recordDay(saved, {
            date: dateKey(),
            total: todayValue,
            byDiscipline: Object.fromEntries(
              DISCIPLINES.map((d) => [d, scoreValue(byDiscipline[d])]),
            ) as Partial<Record<Discipline, number | null>>,
          })
          await store.set(KEYS.indexHistory, saved)
        }
      } catch {
        // история не критична - экран работает и без неё
      }

      if (alive) {
        setGames(result)
        setHistory(saved)
        setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [tick])

  const byDiscipline = collectAll(games)
  const total = honeScore(DISCIPLINES.map((d) => byDiscipline[d]))

  return {
    loading,
    total,
    byDiscipline,
    games,
    sessionsTotal: games.reduce((sum, g) => sum + g.sessions, 0),
    weakest: weakestOf(byDiscipline),
    history,
    weekChange: weekChange(history),
    reload,
  }
}

/** Счёт по каждой дисциплине. Список берётся из DISCIPLINES, а не хардкодом. */
function collectAll(games: readonly GameProgress[]): Record<Discipline, HoneScore> {
  return Object.fromEntries(
    DISCIPLINES.map((d) => [d, collect(d, games)]),
  ) as Record<Discipline, HoneScore>
}

function collect(discipline: Discipline, games: readonly GameProgress[]): HoneScore {
  const contributions: GameContribution[] = []
  for (const g of games) {
    const module = GAMES.find((m) => m.id === g.gameId)
    if (!module || module.discipline !== discipline) continue
    const value = scoreValue(g.score)
    if (value === null) continue
    if (g.score.status !== 'ready') {
      contributions.push({
        score: value,
        sessions: g.sessions,
        lastPlayedAt: g.lastPlayedAt,
        provisional: true,
      })
      continue
    }
    contributions.push({
      score: g.score.value,
      sessions: g.sessions,
      lastPlayedAt: g.lastPlayedAt,
      provisional: false,
    })
  }
  return contributions.length === 0 ? EMPTY : disciplineScore(contributions)
}

/**
 * Отстающая дисциплина. Учитываем и предварительные оценки: после первой
 * тренировки уже видно перекос, и программу дня стоит наклонить сразу,
 * а не ждать пяти заходов.
 */
function weakestOf(by: Readonly<Record<Discipline, HoneScore>>): Discipline | null {
  const known = DISCIPLINES.map((d) => ({ d, v: scoreValue(by[d]) })).filter(
    (x): x is { d: Discipline; v: number } => x.v !== null,
  )
  if (known.length < 2) return null

  const sorted = [...known].sort((a, b) => a.v - b.v)
  const worst = sorted[0] as { d: Discipline; v: number }
  const next = sorted[1] as { d: Discipline; v: number }
  // при равенстве отстающей нет - иначе выбор был бы случайным
  return worst.v === next.v ? null : worst.d
}
